import { ImportColumn } from '../../../api/created/importFile.ts';

// What the reviewed file is imported into. The review page gets it from the search params, where the
// upload dialog puts exactly one of `patientId` and `screeningCaseId`.
export type ImportTarget =
  | { type: 'CONTACT_TRACING'; patientId: string }
  | { type: 'SCREENING'; screeningCaseId: string };

export interface ImportTargetSearchParams {
  // The index patient a contact list was uploaded for.
  patientId?: string;
  // The screening case a person list was uploaded for.
  screeningCaseId?: string;
}

export function getImportTarget({ patientId, screeningCaseId }: ImportTargetSearchParams): ImportTarget | null {
  if (patientId && !screeningCaseId) {
    return { type: 'CONTACT_TRACING', patientId };
  }
  if (screeningCaseId && !patientId) {
    return { type: 'SCREENING', screeningCaseId };
  }
  return null;
}

// The preview and submit requests carry both ids, the one that doesn't apply as null.
export function getImportTargetIds(target: ImportTarget) {
  return target.type === 'CONTACT_TRACING'
    ? { patientId: target.patientId, screeningCaseId: null }
    : { patientId: null, screeningCaseId: target.screeningCaseId };
}

export const IMPORT_TARGET_TEXTS: Record<
  ImportTarget['type'],
  { title: string; noPersons: string; loadError: string; imported: string; alreadyAssigned: string }
> = {
  CONTACT_TRACING: {
    title: 'Kontaktliste prüfen',
    noPersons: 'Die Datei enthält keine Kontakte.',
    loadError: 'Die Kontaktliste konnte nicht geladen werden',
    imported: 'Kontakte importiert',
    alreadyAssigned: 'Die Person ist bereits als Kontakt erfasst',
  },
  SCREENING: {
    title: 'Personenliste prüfen',
    noPersons: 'Die Datei enthält keine Personen.',
    loadError: 'Die Personenliste konnte nicht geladen werden',
    imported: 'Personen importiert',
    alreadyAssigned: 'Die Person ist bereits in diesem Screening erfasst',
  },
};

// The screening template has no "Datum letzter Kontakt" column, and calls the remarks "Bemerkungen".
export function getImportTargetColumns(type: ImportTarget['type']): ImportColumn[] {
  const columns: ImportColumn[] = [
    'GENDER',
    'FIRST_NAME',
    'LAST_NAME',
    'BIRTH_DATE',
    'STREET',
    'POSTAL_CODE',
    'CITY',
    'NATIONALITY',
    'ASYLUM_NUMBER',
    'NOTE',
  ];
  return type === 'CONTACT_TRACING' ? [...columns, 'LAST_CONTACT_DATE'] : columns;
}

export function getNoteColumnLabel(type: ImportTarget['type']) {
  return type === 'CONTACT_TRACING' ? 'Bemerkungen' : 'Bemerkungen';
}
